import { useEffect, useState } from 'react';
import { combineInteractors } from '@updater-core/lib/store/utils/combine';
import {
  CreateInteractor,
  Interactor,
  InteractorMap,
  CreateInteractors,
} from '@updater-core/lib/store/types.d';
/**
 * A hook which will create the interactor on first render-pass
 * and return the created interactor on each subsequent render-pass
 *
 * @param {*} createInteractor the constructor function of the interactor
 * @param {*} dependencies the dependencies of the interactor
 */
export function useCreateInteractor<S, I extends Interactor<S>, D>(
  createInteractor: CreateInteractor<S, I, D>,
  dependencies: D
) {
  const [interactor] = useState(() => createInteractor(dependencies));
  useEffect(() => () => interactor.destroy && interactor.destroy(), []);
  return interactor;
}

/**
 * A hook which will create and combine a collection of interactors
 * on first render-pass and return the created interactor on each subsequent render-pass
 *
 * @param {*} createInteractors a constructor function which returns a
 * collection of interactors to be combined with combineInteractors
 * @param {*} dependencies the dependencies of the interactors
 */
function useCreateInteractors(createInteractors, dependencies) {
  const [interactors] = useState(() =>
    combineInteractors(createInteractors(dependencies))
  );
  useEffect(
    () => () => {
      Object.values(interactors).forEach((interactor) =>
        interactor.destroy?.()
      );
    },
    []
  );
  return interactors;
}

/**
 * A hook which will subscribe to a given interactor
 * and return the state on each interactor update.
 *
 * @param {*} interactor
 */
export function useInteractorState<S>(interactor: Interactor<S>) {
  const [state, setState] = useState(() => interactor.getState());

  useEffect(
    () =>
      interactor.connect((interactorState) => {
        setState(interactorState);
      }),
    [interactor]
  );
  return state;
}

/**
 * A hook which both creates and subscribes to an interactor,
 * returning the interactor with the current state on each stateupdate
 *
 * @param {*} createInteractor the constructor function of the interactor
 * @param {*} dependencies the dependencies of the interactor
 */
export function useInteractor<S, I extends Interactor<S>, D = null>(
  createInteractor: CreateInteractor<S, I, D>,
  dependencies?: D
) {
  const interactor = useCreateInteractor(createInteractor, dependencies);

  const state = useInteractorState(interactor);
  return {
    ...interactor,
    state,
  };
}

/**
 * A hook which both creates and subscribes to a collection of interactors,
 * returning the interactors with the current state on each update
 *
 * @param {*} createInteractors a constructor function which returns a
 * collection of interactors to be combined with combineInteractors
 * @param {*} dependencies the dependencies of the interactors
 */
export function useInteractors(createInteractors, dependencies?) {
  const interactors = useCreateInteractors(createInteractors, dependencies);
  const state = useInteractorState(interactors);
  return {
    ...interactors,
    state,
  };
}

/**
 * @deprecated The method should not be used.
 * A hook which both creates and subscribes to a collection of interactors,
 * returning the interactors with the current state on each update
 *
 * @param {CreateInteractors} createInteractors a constructor function which returns a
 * collection of interactors to be combined with combineInteractors
 * @param {*} dependencies the dependencies of the interactors
 */
export function useComposedInteractor<StateMap extends { [s: string]: any }>(
  createInteractors: CreateInteractors<StateMap>,
  dependencies = {}
): InteractorMap<StateMap> {
  const [interactors] = useState(() => createInteractors(dependencies));
  const keys = Object.keys(interactors) as Array<keyof StateMap>;

  useEffect(
    () => () =>
      keys.forEach((key) => {
        interactors[key].destroy?.();
      }),
    []
  );

  const [state, setState] = useState(() =>
    keys.reduce(
      (accState, key) => ({
        ...accState,
        [key]: interactors[key].getState(),
      }),
      {} as StateMap
    )
  );

  useEffect(() => {
    const unsubscribes = keys.map((childInteractorKey) =>
      // TODO: this will cause rerenders upon initialization however given
      // prop-diffing and the fact that most of our screens aren't that render
      // heavy. However this is a place to look for perf issues.
      interactors[childInteractorKey].connect((childInteractorState) => {
        setState((currState) => ({
          ...currState,
          [childInteractorKey]: childInteractorState,
        }));
      })
    );

    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [interactors, setState]);

  return keys.reduce(
    (composedInteractor, key) => ({
      ...composedInteractor,
      [key]: {
        ...interactors[key],
        state: state[key],
      },
    }),
    {} as InteractorMap<StateMap>
  );
}
